import {
  Box,
  Button,
  Container,
  ContentLayout,
  ExpandableSection,
  Header,
  Input,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { Timestamp } from '@firebase/firestore';
import groupBy from 'lodash/groupBy';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Agp } from '../api/agp';
import { createItem, fetchItems, fetchItemsByIds } from '../api/generic';
import Flex from '../components/Flex';
import PeriodFilterControls, { ViewMode } from '../components/PeriodFilterControls';
import { useAlertContext } from '../contexts/AlertContext';
import { useAuthContext } from '../contexts/AuthContext';
import { Batch } from '../models/Batch';
import { Recipe } from '../models/Recipe';
import { getEndOfQuarter, getEndOfYear, getStartOfQuarter, getStartOfYear } from '../utils/date';
import { prefixCollection } from '../utils/prefix';

interface Movement {
  id: string;
  amount: number;
  date: Date;
  note?: string;
  releasedDate?: Date;
  adjustment?: boolean;
}

interface AgpSizeGroup {
  bottleName: string;
  input: number;
  output: number;
  expected: number;
  movements: Movement[];
}

interface AgpBatchGroup {
  batchId: string;
  sizes: AgpSizeGroup[];
}

interface AgpSlugGroup {
  slug: string;
  batches: AgpBatchGroup[];
}

interface AgpStorageCheckRecord {
  id?: string;
  checkedBy: string;
  date: Timestamp;
  entries: Array<{
    slug: string;
    batchId: string;
    bottleName: string;
    input: number; // sum of AGPs without releasedDate
    output: number; // sum of AGPs with releasedDate
    expected: number; // input - output
    actual: number; // what you counted
    discrepancy: number; // actual - expected
    note?: string;
  }>;
}

// helper to convert Firestore Timestamps safely
const toJsDate = (val: any): Date | undefined => {
  if (!val) return undefined;
  if (val instanceof Date) return val;
  if (typeof val.toDate === 'function') return val.toDate();
  return new Date(val);
};

const AgpStorageCheck: FC = () => {
  const alert = useAlertContext();
  const navigate = useNavigate();
  const { tenant, userObject } = useAuthContext();
  const [agp, setAgp] = useState<Agp[]>([]);

  const [batchMap, setBatchMap] = useState<Record<string, Batch>>({});
  const [recipeMap, setRecipeMap] = useState<Record<string, Recipe>>({});
  const [userMap, setUserMap] = useState<Record<string, { id: string; name?: string; email?: string }>>({});

  const [viewMode, setViewMode] = useState<ViewMode>('quarter');
  const [activeDate, setActiveDate] = useState<Date>(new Date());
  const [pastChecks, setPastChecks] = useState<AgpStorageCheckRecord[]>([]);

  const [actualCounts, setActualCounts] = useState<Record<string, number | undefined>>({});
  const keyFor = (batchId: string, bottleName: string) => `${batchId}::${bottleName}`;

  // load AGPs + batches
  const load = async () => {
    // 1) AGPs
    const agps = await fetchItems<Agp>(prefixCollection('agp', tenant));
    setAgp(agps);

    // 2) Batches for those AGPs
    const batchIds = Array.from(new Set(agps.map((a) => a.batchId).filter(Boolean)));
    const batches = await fetchItemsByIds<Batch>(prefixCollection('batches', tenant), batchIds);
    const bMap: Record<string, Batch> = {};
    for (const b of batches) bMap[b.id] = b;
    setBatchMap(bMap);

    // 3) Recipes for those batches
    const recipeIds = Array.from(new Set(batches.map((b) => b.recipeId).filter(Boolean)));
    if (recipeIds.length) {
      const recipes = await fetchItemsByIds<Recipe>(prefixCollection('recipes', tenant), recipeIds);
      const rMap: Record<string, Recipe> = {};
      for (const r of recipes) rMap[r.id] = r;
      setRecipeMap(rMap);
    } else {
      setRecipeMap({});
    }
  };

  useEffect(() => {
    load();
  }, [tenant]);

  const loadChecks = async () => {
    const getPeriodBounds = (): [Date, Date] => {
      if (viewMode === 'quarter') return [getStartOfQuarter(activeDate), getEndOfQuarter(activeDate)];
      return [getStartOfYear(activeDate), getEndOfYear(activeDate)];
    };
    const all = await fetchItems<AgpStorageCheckRecord>(prefixCollection('agpStorageChecks', tenant));
    const filtered = all.filter((c) => {
      const d = toJsDate(c.date);
      const [start, end] = getPeriodBounds();
      return d && d >= start && d <= end;
    });
    setPastChecks(filtered);

    const userIds = Array.from(new Set(filtered.map((c) => c.checkedBy).filter(Boolean)));
    if (userIds.length) {
      const users = await fetchItemsByIds<{ id: string; name?: string; email?: string }>('users', userIds);
      const u: Record<string, any> = {};
      for (const usr of users) u[usr.id] = usr;
      setUserMap(u);
    }
  };

  // load past checks
  useEffect(() => {
    loadChecks();
  }, [tenant, viewMode, activeDate]);

  const getSlugForBatch = (batchId: string): string => {
    const b = batchMap[batchId];
    if (!b) return 'unknown';
    // try embedded recipe first, then explicit batch field, then recipeMap
    return (
      b.recipe?.slug ?? (b as any).recipeSlug ?? (b.recipeId ? recipeMap[b.recipeId]?.slug : undefined) ?? 'unknown'
    );
  };

  // group AGPs by slug → batch → size
  const grouped: AgpSlugGroup[] = Object.entries(groupBy(agp, (a) => getSlugForBatch(a.batchId))).map(
    ([slug, slugEntries]) => ({
      slug,
      batches: Object.entries(groupBy(slugEntries, 'batchId')).map(([batchId, batchEntries]) => ({
        batchId,
        sizes: Object.entries(groupBy(batchEntries, 'name')).map(([bottleName, sizeEntries]) => {
          const movements: Movement[] = sizeEntries
            .map((e) => {
              let amount = e.amount ?? 0;
              let releasedDate = toJsDate(e.releasedDate);

              // If input (no releasedDate) but amount < 0 → treat as output
              if (!releasedDate && amount < 0) {
                releasedDate = toJsDate(e.createdDate) ?? new Date();
                amount = Math.abs(amount);
              }

              return {
                id: e.id!,
                amount,
                date: toJsDate(e.createdDate) ?? new Date(),
                note: e.note,
                releasedDate,
              };
            })
            .sort((a, b) => a.date.getTime() - b.date.getTime());

          const input = movements.filter((m) => !m.releasedDate).reduce((s, m) => s + m.amount, 0);
          const output = movements.filter((m) => m.releasedDate).reduce((s, m) => s + m.amount, 0);

          return { bottleName, input, output, expected: input - output, movements };
        }),
      })),
    }),
  );

  const buildCheckEntries = () => {
    return grouped.flatMap((g) =>
      g.batches.flatMap((b) =>
        b.sizes.map((s) => {
          const key = `${b.batchId}::${s.bottleName}`;
          const actual = actualCounts[key] ?? s.expected; // default actual to expected if untouched
          return {
            slug: g.slug,
            batchId: b.batchId,
            bottleName: s.bottleName,
            input: s.input,
            output: s.output,
            expected: s.expected,
            actual,
            discrepancy: actual - s.expected,
          };
        }),
      ),
    );
  };

  const createAgpAdjustment = async (
    batchId: string,
    bottleName: string,
    amount: number,
    note = 'Inventory adjustment',
  ) => {
    const sizeFromExisting =
      agp.find((a) => a.batchId === batchId && a.name === bottleName)?.size ??
      (bottleName.includes('750') ? 0.75 : 0.33);

    // 👇 OMIT 'id' completely
    const payload = {
      batchId,
      name: bottleName,
      size: sizeFromExisting,
      amount, // + adds stock, - removes stock
      note,
      createdBy: userObject?.id ?? 'unknown',
      createdDate: Timestamp.now(),
      // releasedDate: undefined,  // also omit if not set
    };

    await createItem(prefixCollection('agp', tenant), payload);
  };

  const handleRecordOnly = async () => {
    const entries = buildCheckEntries();
    const record: AgpStorageCheckRecord = {
      checkedBy: userObject?.id ?? 'unknown',
      date: Timestamp.now(),
      entries,
    };
    await createItem(prefixCollection('agpStorageChecks', tenant), record);
    await loadChecks();
    alert.success('Inventory check recorded (no AGP updates).');
  };

  const handleRecordAndUpdate = async () => {
    const entries = buildCheckEntries();
    const record: AgpStorageCheckRecord = {
      checkedBy: userObject?.id ?? 'unknown',
      date: Timestamp.now(),
      entries,
    };
    await createItem(prefixCollection('agpStorageChecks', tenant), record);

    // write adjustments only where discrepancy !== 0
    for (const e of entries) {
      if (e.discrepancy !== 0) {
        await createAgpAdjustment(e.batchId, e.bottleName, e.discrepancy);
      }
    }

    const agps = await fetchItems<Agp>(prefixCollection('agp', tenant));
    setAgp(agps);

    await load();
    await loadChecks();

    alert.success('Inventory check recorded and AGP updated.');
  };

  const handlePeriodChange = (dir: 'prev' | 'next') => {
    setActiveDate((prev) => {
      const month = prev.getMonth();
      const year = prev.getFullYear();
      if (viewMode === 'quarter') {
        return new Date(year, dir === 'prev' ? month - 3 : month + 3, 1);
      } else {
        return new Date(dir === 'prev' ? year - 1 : year + 1, 0, 1);
      }
    });
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <Button iconName="arrow-left" variant="normal" onClick={() => navigate('/agp')}>
              Back
            </Button>
          }
        >
          AGP Storage Check
        </Header>
      }
    >
      {grouped.map((slugGroup) => {
        return (
          <div key={slugGroup.slug} style={{ marginBottom: '1rem' }}>
            <Container>
              <SpaceBetween size="l">
                <Header variant="h2">{slugGroup.slug}</Header>

                {slugGroup.batches.map((batch) => (
                  <SpaceBetween key={batch.batchId} size="m">
                    <Header variant="h3">
                      <Flex>
                        {batchMap[batch.batchId]?.batchId ?? '...'} – {batchMap[batch.batchId]?.name ?? 'Unknown'}
                        <Box variant="awsui-gen-ai-label">{batch.batchId}</Box>
                      </Flex>
                    </Header>

                    <Table<AgpSizeGroup>
                      columnDefinitions={[
                        {
                          header: 'Bottle',
                          cell: (i) => (
                            <ExpandableSection headerText={i.bottleName} variant="inline">
                              <Table<Movement>
                                columnDefinitions={[
                                  { header: 'Date', cell: (m) => m.date.toLocaleDateString() },
                                  { header: 'Amount', cell: (m) => m.amount },
                                  {
                                    header: 'Direction',
                                    cell: (m) =>
                                      m.releasedDate ? (
                                        <span style={{ color: 'red', fontWeight: 600 }}>OUT</span>
                                      ) : (
                                        <span style={{ color: 'green', fontWeight: 600 }}>IN</span>
                                      ),
                                  },
                                  { header: 'Note', cell: (m) => m.note ?? '' },
                                ]}
                                items={i.movements}
                                trackBy="id"
                                variant="embedded"
                              />
                            </ExpandableSection>
                          ),
                        },
                        {
                          header: 'Input',
                          cell: (i) => <span style={{ color: 'green', fontWeight: 600 }}>{i.input}</span>,
                        },
                        {
                          header: 'Output',
                          cell: (i) => <span style={{ color: 'red', fontWeight: 600 }}>{i.output}</span>,
                        },
                        { header: 'Expected', cell: (i) => i.expected },
                        {
                          header: 'Actual',
                          cell: (i) => {
                            const k = keyFor(batch.batchId, i.bottleName);
                            return (
                              <Input
                                type="number"
                                value={actualCounts[k]?.toString() ?? ''}
                                placeholder="count"
                                onChange={({ detail }) =>
                                  setActualCounts((prev) => ({
                                    ...prev,
                                    [k]: detail.value === '' ? undefined : Number(detail.value),
                                  }))
                                }
                              />
                            );
                          },
                        },
                        {
                          header: 'Discrepancy',
                          cell: (i) => {
                            const k = keyFor(batch.batchId, i.bottleName);
                            const actual = actualCounts[k];
                            if (actual === undefined) return '';
                            const diff = actual - i.expected;
                            if (diff === 0) return '✓';
                            return (
                              <span style={{ color: diff > 0 ? 'green' : 'red', fontWeight: 600 }}>
                                {diff > 0 ? `+${diff}` : diff}
                              </span>
                            );
                          },
                        },
                      ]}
                      items={batch.sizes}
                      trackBy={(item) => `${batch.batchId}-${item.bottleName}`}
                      variant="embedded"
                    />
                  </SpaceBetween>
                ))}
              </SpaceBetween>
            </Container>
          </div>
        );
      })}

      <SpaceBetween direction="vertical" size="m">
        <SpaceBetween direction="horizontal" size="s">
          <Button onClick={handleRecordOnly}>Record only</Button>
          <Button variant="primary" onClick={handleRecordAndUpdate}>
            Record & update AGP
          </Button>
        </SpaceBetween>

        <Header variant="h2">Past AGP Checks</Header>
        <PeriodFilterControls
          viewMode={viewMode}
          setViewMode={setViewMode}
          activeDate={activeDate}
          handlePeriodChange={handlePeriodChange}
        />

        <Table<AgpStorageCheckRecord>
          columnDefinitions={[
            { header: 'Date', cell: (c) => toJsDate(c.date)?.toLocaleDateString() },
            {
              header: 'Checked By',
              cell: (c) => userMap[c.checkedBy]?.name ?? userMap[c.checkedBy]?.email ?? c.checkedBy,
            },
            { header: 'Entries', cell: (c) => c.entries.length },
          ]}
          items={pastChecks}
          stickyHeader
          variant="borderless"
          trackBy="id"
        />
      </SpaceBetween>
    </ContentLayout>
  );
};

export default AgpStorageCheck;
